import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface SwipeRecord {
  id: string;
  user_id: string;
  swiped_user_id: string;
  direction: "left" | "right";
  is_super_like: boolean;
  created_at: string;
}

export const useSwipeHistory = () => {
  const { user } = useAuth();
  const [swipeHistory, setSwipeHistory] = useState<SwipeRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchSwipeHistory = async () => {
      const { data, error } = await supabase
        .from("swipe_history")
        .select("*")
        .eq("user_id", user.id);

      if (!error && data) {
        setSwipeHistory(data as SwipeRecord[]);
      }
      setLoading(false);
    };

    fetchSwipeHistory();
  }, [user]);

  const saveSwipe = async (
    swipedUserId: string, 
    direction: "left" | "right",
    isSuperLike: boolean = false
  ) => {
    if (!user) return;

    const { data, error } = await supabase
      .from("swipe_history")
      .upsert(
        {
          user_id: user.id,
          swiped_user_id: swipedUserId,
          direction,
          is_super_like: isSuperLike,
        },
        { onConflict: "user_id,swiped_user_id" }
      )
      .select()
      .single();

    if (!error && data) {
      setSwipeHistory((prev) => {
        const filtered = prev.filter((s) => s.swiped_user_id !== swipedUserId);
        return [...filtered, data as SwipeRecord];
      });
    }

    return { data, error };
  };

  const undoSwipe = async (swipedUserId: string) => {
    if (!user) return;

    const { error } = await supabase
      .from("swipe_history")
      .delete()
      .eq("user_id", user.id)
      .eq("swiped_user_id", swipedUserId);

    if (!error) {
      setSwipeHistory((prev) => 
        prev.filter((s) => s.swiped_user_id !== swipedUserId)
      );
    }

    return { error };
  };

  const getSwipedUserIds = () => {
    return swipeHistory.map((s) => s.swiped_user_id);
  };

  const getLikedUserIds = () => {
    return swipeHistory
      .filter((s) => s.direction === "right")
      .map((s) => s.swiped_user_id);
  };

  // Get liked user IDs sorted by most recent first
  const getLikedUserIdsSorted = () => {
    return swipeHistory
      .filter((s) => s.direction === "right")
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .map((s) => s.swiped_user_id);
  };

  // Check if a user was matched within the last 24 hours
  const isRecentMatch = (userId: string) => {
    const record = swipeHistory.find(
      (s) => s.swiped_user_id === userId && s.direction === "right"
    );
    if (!record) return false;
    const matchedAt = new Date(record.created_at).getTime();
    const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
    return matchedAt > twentyFourHoursAgo;
  };

  const hasSwipedUser = (userId: string) => {
    return swipeHistory.some((s) => s.swiped_user_id === userId);
  };

  const getLastSwipedUserId = () => {
    if (swipeHistory.length === 0) return null;
    const sorted = [...swipeHistory].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    return sorted[0]?.swiped_user_id || null;
  };

  return {
    swipeHistory,
    loading,
    saveSwipe,
    undoSwipe,
    getSwipedUserIds,
    getLikedUserIds,
    getLikedUserIdsSorted,
    isRecentMatch,
    hasSwipedUser,
    getLastSwipedUserId,
  };
};
